import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { smsSourceLabel } from './SmsConsentBadge';
import { inZone } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

// "Text messages" on the customer edit form (SMS Phase 1, owner and admin).
//
// Shows the consent state with when and how, a history of every change from
// sms_consent_events, the owner's "Customer agreed to texts" control (which
// needs the attestation tick, and records who ticked it), and a manual
// opt-out. Consent belongs to the customer, not a property: the server
// resolves a property id to its customer, so this field always shows one fact.
//
// Every change is its own request and is saved immediately, apart from the
// rest of the form: consent is evidence, and it should not depend on the
// owner also pressing Save Changes.
const fmt = (iso, timezone) => (iso ? new Date(iso).toLocaleString('en-US', inZone(timezone, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
})) : '');

const EVENT_LABELS = { opt_in: 'Agreed to texts', opt_out: 'Opted out' };
const EVENT_SOURCE_LABELS = { keyword_stop: 'texted STOP', carrier_21610: 'carrier reported opt-out' };

function SmsConsentField({ customerId, onChange }) {
  const timezone = useCompanyTimezone();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [attesting, setAttesting] = useState(false);
  const [attested, setAttested] = useState(false);

  const url = `${process.env.REACT_APP_API_URL}/api/customers/${customerId}/sms-consent`;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(url);
      setState(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load text message consent');
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const submit = async (action) => {
    if (action === 'opt_out' && !window.confirm('Record that this customer does not want text messages?')) return;
    setBusy(true);
    try {
      const { data } = await axios.put(url, action === 'opt_in' ? { action, attested } : { action });
      setState(data);
      setAttesting(false);
      setAttested(false);
      setError('');
      if (onChange) onChange(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save text message consent');
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="form-group">
        <label className="form-label">Text messages</label>
        {error ? <div className="text-muted" style={{ fontSize: '12px' }}>{error}</div>
          : <div className="text-muted" style={{ fontSize: '12px' }}>Loading...</div>}
      </div>
    );
  }

  const optedIn = state.smsConsent === 'opted_in';
  const summary = optedIn
    ? `Agreed to texts ${fmt(state.smsConsentAt, timezone)} (${smsSourceLabel(state.smsConsentSource)})`
    : state.smsConsent === 'opted_out'
      ? `Opted out ${fmt(state.smsOptedOutAt, timezone)}`
      : 'No consent recorded';

  return (
    <div className="form-group sms-consent-field">
      <label className="form-label">Text messages</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <span>{summary}</span>
        {state.events.length > 0 && (
          <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? 'Hide history' : `History (${state.events.length})`}
          </button>
        )}
        {!optedIn && !attesting && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => setAttesting(true)}
            disabled={busy || !state.hasTextablePhone}
            title={state.hasTextablePhone ? undefined : 'Save a valid 10-digit phone number first'}
          >
            Customer agreed to texts
          </button>
        )}
        {optedIn && (
          <button type="button" className="btn btn-sm btn-outline" onClick={() => submit('opt_out')} disabled={busy}>
            Record opt-out
          </button>
        )}
      </div>
      {!state.hasTextablePhone && !optedIn && (
        <span className="text-muted" style={{ fontSize: '12px' }}>
          Texts need a valid 10-digit phone number on this customer.
        </span>
      )}
      {optedIn && !state.canText && (
        <span className="text-muted" style={{ fontSize: '12px' }}>
          Not textable right now (no valid phone number, or marked Do Not Serve).
        </span>
      )}

      {attesting && (
        <div style={{ marginTop: '8px', padding: '8px', border: '1px solid var(--color-border, #ddd)', borderRadius: '6px' }}>
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
            <span>{state.attestationText}</span>
          </label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => submit('opt_in')} disabled={busy || !attested}>
              {busy ? 'Saving...' : 'Record consent'}
            </button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => { setAttesting(false); setAttested(false); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <ul className="sms-consent-history" style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px' }}>
          {state.events.map((e) => (
            <li key={e.id} style={{ marginBottom: '6px' }}>
              <strong>{EVENT_LABELS[e.event] || e.event}</strong>
              {' '}{fmt(e.createdAt, timezone)}
              {' - '}{EVENT_SOURCE_LABELS[e.source] || smsSourceLabel(e.source)}
              {e.recordedBy ? ` by ${e.recordedBy}` : ''}
              {e.consentText && (
                <div className="text-muted" style={{ fontStyle: 'italic' }}>&ldquo;{e.consentText}&rdquo;</div>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <div className="alert alert-error" style={{ marginTop: '8px' }}>{error}</div>}
    </div>
  );
}

export default SmsConsentField;
